import DeleteIcon from '@mui/icons-material/Delete';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { apiRawFetch } from '../../main/client';

interface GlobalAdminPermission {
    type: 'globalAdmin';
}

interface PropertyAdminPermission {
    type: 'propertyAdmin';
    propertyId: number;
}

type Permission = GlobalAdminPermission | PropertyAdminPermission;

interface Property {
    id: number;
    name: string;
}

interface User {
    id: number;
    username: string;
    name: string;
    permissions: Permission[];
    created: string;
    updated: string;
}

interface UserFormDialogProps {
    open: boolean;
    user: User | null;
    currentUserPermissions: Permission[];
    onClose: () => void;
    onSaved: () => void;
    onDeleted?: () => void;
}

const UserFormDialog = ({
    open,
    user,
    currentUserPermissions,
    onClose,
    onSaved,
    onDeleted,
}: UserFormDialogProps) => {
    const [username, setUsername] = useState('');
    const [name, setName] = useState('');
    const [password, setPassword] = useState('');
    const [isGlobalAdmin, setIsGlobalAdmin] = useState(false);
    const [propertyAdminIds, setPropertyAdminIds] = useState<number[]>([]);
    const [properties, setProperties] = useState<Property[]>([]);
    const [selectedPropertyId, setSelectedPropertyId] = useState<number | ''>('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const isEditing = user !== null;
    const currentUserIsGlobalAdmin = currentUserPermissions.some(p => p.type === 'globalAdmin');
    const currentUserPropertyIds = currentUserPermissions
        .filter((p): p is PropertyAdminPermission => p.type === 'propertyAdmin')
        .map(p => p.propertyId);

    // Fetch properties for the selector
    useEffect(() => {
        if (!open) return;

        const fetchProperties = async () => {
            try {
                const response = await apiRawFetch('api/properties');
                if (response.ok) {
                    const data = await response.json();
                    setProperties(data as Property[]);
                }
            } catch {
                // Silently fail - properties won't be available for selection
            }
        };

        fetchProperties();
    }, [open]);

    useEffect(() => {
        if (user) {
            setUsername(user.username);
            setName(user.name);
            setPassword('');
            // Extract permissions
            setIsGlobalAdmin(user.permissions.some(p => p.type === 'globalAdmin'));
            setPropertyAdminIds(
                user.permissions
                    .filter((p): p is PropertyAdminPermission => p.type === 'propertyAdmin')
                    .map(p => p.propertyId)
            );
        } else {
            setUsername('');
            setName('');
            setPassword('');
            setIsGlobalAdmin(false);
            setPropertyAdminIds([]);
        }
        setSelectedPropertyId('');
        setError(null);
    }, [user, open]);

    const handleAddProperty = () => {
        if (selectedPropertyId !== '' && !propertyAdminIds.includes(selectedPropertyId)) {
            setPropertyAdminIds([...propertyAdminIds, selectedPropertyId]);
            setSelectedPropertyId('');
        }
    };

    const handleRemoveProperty = (propertyId: number) => {
        setPropertyAdminIds(propertyAdminIds.filter(id => id !== propertyId));
    };

    // Filter available properties: global admins see all, property admins see only their properties
    const availableProperties = currentUserIsGlobalAdmin
        ? properties
        : properties.filter(p => currentUserPropertyIds.includes(p.id));

    // Properties that can be added (not already selected)
    const selectableProperties = availableProperties.filter(
        p => !propertyAdminIds.includes(p.id)
    );

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (
            !username.trim() ||
            !name.trim() ||
            (!isEditing && !password.trim())
        ) {
            setError('All fields are required');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const endpoint =
                isEditing && user ? `api/users/${user.id}` : 'api/users';

            const body: Record<string, string> = {
                username: username.trim(),
                name: name.trim(),
            };

            if (password.trim()) {
                body.password = password.trim();
            }

            const response = await apiRawFetch(endpoint, 'POST', body);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(
                    (errorData as { error?: string }).error ??
                        'Failed to save user'
                );
            }

            // For existing users, also update permissions
            if (isEditing && user) {
                const permissions: Permission[] = [];
                if (isGlobalAdmin) {
                    permissions.push({ type: 'globalAdmin' });
                }
                for (const propId of propertyAdminIds) {
                    permissions.push({ type: 'propertyAdmin', propertyId: propId });
                }

                const permResponse = await apiRawFetch(
                    `api/users/${user.id}/permissions`,
                    'POST',
                    { permissions }
                );

                if (!permResponse.ok) {
                    const errorData = await permResponse.json().catch(() => ({}));
                    throw new Error(
                        (errorData as { error?: string }).error ??
                            'Failed to save permissions'
                    );
                }
            }

            onSaved();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Unknown error');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!user || !onDeleted) return;

        try {
            setLoading(true);
            setError(null);

            const response = await apiRawFetch(
                `api/users/${user.id}/delete`,
                'POST'
            );

            if (!response.ok) {
                throw new Error('Failed to delete user');
            }

            onDeleted();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete user');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            onClose();
        }
    };

    const textFieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: 0,
            '& input': {
                backgroundColor: '#0f0f23',
            },
            '& fieldset': {
                borderWidth: '2px',
                borderColor: '#374151',
            },
            '&:hover fieldset': {
                borderWidth: '2px',
                borderColor: '#6366f1',
            },
            '&.Mui-focused fieldset': {
                borderWidth: '3px',
                borderColor: '#6366f1',
            },
        },
        '& .MuiInputLabel-root': {
            fontWeight: 700,
            textTransform: 'uppercase',
            fontSize: '1rem',
            letterSpacing: '0.05em',
        },
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 0,
                        border: '4px solid #6366f1',
                        backgroundColor: '#1a1a2e',
                    },
                },
            }}
        >
            <form onSubmit={handleSubmit}>
                <DialogTitle
                    sx={{
                        borderBottom: '3px solid #6366f1',
                        backgroundColor: '#0f0f23',
                        fontWeight: 900,
                        fontSize: '1.5rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        py: 2.5,
                    }}
                >
                    {isEditing ? 'Edit User' : 'Create New User'}
                </DialogTitle>
                <DialogContent
                    sx={{
                        backgroundColor: '#1a1a2e',
                        p: 0,
                    }}
                >
                    <Stack sx={{ py: 3, px: 3 }} spacing={2.5}>
                        {error && (
                            <Alert
                                severity="error"
                                onClose={() => setError(null)}
                                sx={{
                                    borderRadius: 0,
                                    border: '3px solid #ef4444',
                                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                    fontWeight: 600,
                                    '& .MuiAlert-icon': {
                                        color: '#ef4444',
                                    },
                                }}
                            >
                                {error}
                            </Alert>
                        )}
                        <TextField
                            autoFocus
                            label="Username"
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={username}
                            onChange={e => setUsername(e.target.value)}
                            disabled={loading}
                            required
                            sx={textFieldSx}
                        />
                        <TextField
                            label="Full Name"
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            disabled={loading}
                            required
                            sx={textFieldSx}
                        />
                        <TextField
                            label={
                                isEditing
                                    ? 'New Password (leave empty to keep current)'
                                    : 'Password'
                            }
                            type="password"
                            fullWidth
                            variant="outlined"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            disabled={loading}
                            required={!isEditing}
                            sx={textFieldSx}
                        />

                        {/* Permissions Section - Only shown when editing */}
                        {isEditing && (
                            <Box
                                sx={{
                                    border: '2px solid #374151',
                                    p: 2,
                                    mt: 2,
                                }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        mb: 2,
                                        color: '#9ca3af',
                                    }}
                                >
                                    Permissions
                                </Typography>

                                {/* Global Admin Checkbox - only visible to global admins */}
                                {currentUserIsGlobalAdmin && (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={isGlobalAdmin}
                                                onChange={e => setIsGlobalAdmin(e.target.checked)}
                                                disabled={loading}
                                                sx={{
                                                    color: '#6366f1',
                                                    '&.Mui-checked': {
                                                        color: '#6366f1',
                                                    },
                                                }}
                                            />
                                        }
                                        label="Global Admin"
                                        sx={{
                                            '& .MuiFormControlLabel-label': {
                                                fontWeight: 600,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.05em',
                                                fontSize: '0.875rem',
                                            },
                                        }}
                                    />
                                )}

                                {/* Property Admin Section */}
                                <Box sx={{ mt: 2 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            mb: 1,
                                            color: '#9ca3af',
                                        }}
                                    >
                                        Property Access
                                    </Typography>

                                    {/* Selected Properties */}
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                        {propertyAdminIds.map(propId => {
                                            const property = properties.find(p => p.id === propId);
                                            return (
                                                <Chip
                                                    key={propId}
                                                    label={property?.name ?? `Property ${propId}`}
                                                    onDelete={() => handleRemoveProperty(propId)}
                                                    disabled={loading}
                                                    sx={{
                                                        borderRadius: 0,
                                                        border: '2px solid #6366f1',
                                                        backgroundColor: 'rgba(99, 102, 241, 0.1)',
                                                        fontWeight: 600,
                                                        '& .MuiChip-deleteIcon': {
                                                            color: '#6366f1',
                                                        },
                                                    }}
                                                />
                                            );
                                        })}
                                        {propertyAdminIds.length === 0 && (
                                            <Typography
                                                variant="body2"
                                                sx={{ color: '#6b7280', fontStyle: 'italic' }}
                                            >
                                                No properties assigned
                                            </Typography>
                                        )}
                                    </Box>

                                    {/* Add Property Selector */}
                                    {selectableProperties.length > 0 && (
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Select
                                                value={selectedPropertyId}
                                                onChange={e => setSelectedPropertyId(e.target.value as number | '')}
                                                displayEmpty
                                                disabled={loading}
                                                sx={{
                                                    flex: 1,
                                                    borderRadius: 0,
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderWidth: '2px',
                                                        borderColor: '#374151',
                                                    },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: '#6366f1',
                                                    },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderWidth: '2px',
                                                        borderColor: '#6366f1',
                                                    },
                                                }}
                                            >
                                                <MenuItem value="" disabled>
                                                    Select a property
                                                </MenuItem>
                                                {selectableProperties.map(prop => (
                                                    <MenuItem key={prop.id} value={prop.id}>
                                                        {prop.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            <Button
                                                onClick={handleAddProperty}
                                                disabled={loading || selectedPropertyId === ''}
                                                variant="outlined"
                                                sx={{
                                                    borderRadius: 0,
                                                    borderWidth: '2px',
                                                    borderColor: '#6366f1',
                                                    fontWeight: 700,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.05em',
                                                    '&:hover': {
                                                        borderWidth: '2px',
                                                        borderColor: '#6366f1',
                                                        backgroundColor: 'rgba(99, 102, 241, 0.1)',
                                                    },
                                                }}
                                            >
                                                Add
                                            </Button>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions
                    sx={{
                        borderTop: '3px solid #374151',
                        backgroundColor: '#0f0f23',
                        p: 3,
                        gap: 1.5,
                        justifyContent:
                            isEditing && onDeleted ? 'space-between' : 'flex-end',
                    }}
                >
                    {isEditing && onDeleted && (
                        <Button
                            onClick={handleDelete}
                            disabled={loading}
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            sx={{
                                borderRadius: 0,
                                borderWidth: '2px',
                                borderColor: 'error.main',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                px: 3,
                                py: 1,
                                '&:hover': {
                                    borderWidth: '2px',
                                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                },
                            }}
                        >
                            Delete
                        </Button>
                    )}
                    <div style={{ display: 'flex', gap: '12px' }}>
                        <Button
                            onClick={handleClose}
                            disabled={loading}
                            variant="outlined"
                            sx={{
                                borderRadius: 0,
                                borderWidth: '2px',
                                borderColor: '#374151',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                px: 3,
                                py: 1,
                                '&:hover': {
                                    borderWidth: '2px',
                                    borderColor: '#374151',
                                    backgroundColor: 'rgba(55, 65, 81, 0.1)',
                                },
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={loading}
                            sx={{
                                borderRadius: 0,
                                border: '2px solid #6366f1',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                px: 3,
                                py: 1,
                            }}
                        >
                            {loading
                                ? 'Saving...'
                                : isEditing
                                  ? 'Update'
                                  : 'Create'}
                        </Button>
                    </div>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default UserFormDialog;
